import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, FileText, CreditCard, Building2, BarChart3, Settings, LogOut, Menu, X, Receipt, Eye, TrendingDown, Package, Wallet, Clock, History, ListTodo, Users, FileSpreadsheet, Tag, Wine, UtensilsCrossed, Coffee, Boxes, Warehouse, Bell, ClipboardCheck, BookOpen, GlassWater, Banknote, Scale, ShieldCheck } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { BACKEND_URL } from '@/lib/api';

const API = BACKEND_URL;

// Map module keys to menu items
const MODULE_TO_MENU = {
  dashboard: { name: 'Dashboard', path: '/', icon: LayoutDashboard },
  day_closing: { name: 'Day Closing', path: '/day-closing', icon: Receipt },
  view_closings: { name: 'View Closings', path: '/view-closings', icon: Eye },
  audit_checklist: { name: 'Audit Checklist', path: '/audit-checklist', icon: ClipboardCheck },
  ceo_audit: { name: 'CEO Audit', path: '/ceo-audit', icon: ShieldCheck },
  expense_ledger: { name: 'Expense Ledger', path: '/expense-ledger', icon: TrendingDown },
  expense_pipeline: { name: 'Expense Pipeline', path: '/expense-pipeline', icon: ListTodo },
  credit_ledger: { name: 'Credit Ledger', path: '/credit-ledger', icon: Package },
  bank_management: { name: 'Bank Management', path: '/banks', icon: Building2 },
  pay_bills: { name: 'Pay Bills', path: '/pay-bills', icon: Wallet },
  opening_liabilities: { name: 'Opening Liabilities', path: '/opening-liabilities', icon: History },
  vendor_aging: { name: 'Vendor Aging', path: '/vendor-aging', icon: Clock },
  cash_flow: { name: 'Cash Flow', path: '/cash-flow', icon: Banknote },
  reconciliation: { name: 'Reconciliation', path: '/reconciliation', icon: Scale },
  price_master: { name: 'Price Master', path: '/price-master', icon: Tag },
  na_price_master: { name: 'NA-Price Master', path: '/na-price-master', icon: Boxes },
  store_ledger: { name: 'Store Ledger', path: '/store-ledger', icon: Warehouse },
  recipe_ledger: { name: 'Recipe Ledger', path: '/recipe-ledger', icon: BookOpen },
  crockery: { name: 'Crockery', path: '/crockery', icon: GlassWater },
  bar_master: { name: 'Bar Master', path: '/bar-master', icon: Wine },
  kitchen_master: { name: 'Kitchen Master', path: '/kitchen-master', icon: UtensilsCrossed },
  cafe_master: { name: 'Cafe Master', path: '/cafe-master', icon: Coffee },
  salary_master: { name: 'Salary Master', path: '/salary-master', icon: Users },
  payroll_report: { name: 'Payroll Report', path: '/payroll-report', icon: FileSpreadsheet },
  settings: { name: 'Settings', path: '/settings', icon: Settings },
};

// Report permission keys - user needs at least one to see Reports menu
const REPORT_PERMISSIONS = [
  'report_sales', 'report_expenses', 'report_profit_loss', 'report_vendor',
  'report_bank', 'report_salary', 'report_inventory', 'report_daily_summary',
  'report_monthly_summary', 'report_gst'
];

// Helper to check if user has any report permission
const hasAnyReportPermission = (permissions) => {
  return REPORT_PERMISSIONS.some(key => permissions[key]);
};

// Order of modules in sidebar
const MODULE_ORDER = [
  'dashboard',
  'audit_checklist',
  'day_closing',
  'view_closings',
  'expense_ledger',
  'expense_pipeline',
  'credit_ledger',
  'price_master',
  'na_price_master',
  'store_ledger',
  'recipe_ledger',
  'crockery',
  'bar_master',
  'kitchen_master',
  'cafe_master',
  'bank_management',
  'opening_liabilities',
  'pay_bills',
  'cash_flow',
  'reconciliation',
  'salary_master',
  'payroll_report',
  'vendor_aging',
  'settings',
];

// Fallback role-based menu - now includes Reports as a separate item
const getRoleBasedMenu = (role) => {
  const roleMenus = {
    cashier: ['dashboard', 'day_closing', 'view_closings', 'expense_pipeline', 'pay_bills', 'report_daily_summary'],
    storekeeper: ['dashboard', 'store_ledger', 'credit_ledger', 'price_master', 'na_price_master', 'recipe_ledger', 'crockery', 'expense_pipeline', 'report_inventory'],
    bartender: ['dashboard', 'bar_master'],
    kitchen: ['dashboard', 'kitchen_master'],
    cafe: ['dashboard', 'cafe_master'],
    team_member: ['dashboard', 'day_closing', 'view_closings', 'expense_pipeline', 'bar_master', 'kitchen_master', 'cafe_master'],
    accountant: ['dashboard', 'audit_checklist', 'view_closings', 'bank_management', 'expense_ledger', 'expense_pipeline', 'credit_ledger', 'price_master', 'opening_liabilities', 'pay_bills', 'cash_flow', 'salary_master', 'payroll_report', 'vendor_aging', 'report_sales', 'report_expenses', 'report_profit_loss', 'report_vendor', 'report_bank', 'report_salary', 'report_daily_summary', 'report_monthly_summary', 'report_gst'],
    general_manager: ['dashboard', 'audit_checklist', 'view_closings', 'expense_ledger', 'expense_pipeline', 'credit_ledger', 'price_master', 'store_ledger', 'crockery', 'bar_master', 'kitchen_master', 'cafe_master', 'bank_management', 'pay_bills', 'salary_master', 'payroll_report', 'vendor_aging', 'report_sales', 'report_expenses', 'report_profit_loss', 'report_vendor', 'report_bank', 'report_salary', 'report_inventory', 'report_daily_summary', 'report_monthly_summary', 'report_gst'],
    partner: ['dashboard', 'audit_checklist', 'view_closings', 'expense_ledger', 'credit_ledger', 'price_master', 'store_ledger', 'bar_master', 'kitchen_master', 'cafe_master', 'bank_management', 'cash_flow', 'salary_master', 'payroll_report', 'vendor_aging', 'report_sales', 'report_expenses', 'report_profit_loss', 'report_vendor', 'report_bank', 'report_salary', 'report_inventory', 'report_daily_summary', 'report_monthly_summary', 'report_gst'],
  };
  
  const moduleKeys = roleMenus[role] || ['dashboard'];
  // Check if role has any report permissions
  const hasReports = moduleKeys.some(key => REPORT_PERMISSIONS.includes(key));
  
  // Build menu with Reports item inserted after view_closings
  const menu = [];
  moduleKeys.forEach(key => {
    if (MODULE_TO_MENU[key]) {
      menu.push(MODULE_TO_MENU[key]);
    }
    // Insert Reports after view_closings if role has any report permissions
    if (key === 'view_closings' && hasReports) {
      menu.push({ name: 'Reports', path: '/reports', icon: BarChart3 });
    }
  });
  
  return menu;
};

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [userPermissions, setUserPermissions] = useState(null);
  const [menuItems, setMenuItems] = useState([]);

  // Fetch user permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      // Admin always has full access - use static menu with Reports
      // Cash Flow is included in MODULE_ORDER and admin has all permissions
      if (user?.role === 'admin') {
        const adminMenu = [];
        MODULE_ORDER.forEach(key => {
          if (MODULE_TO_MENU[key]) adminMenu.push(MODULE_TO_MENU[key]);
          // Insert Reports after view_closings for admin
          if (key === 'view_closings') {
            adminMenu.push({ name: 'Reports', path: '/reports', icon: BarChart3 });
          }
          // Insert CEO Audit after audit_checklist for admin only
          if (key === 'audit_checklist') {
            adminMenu.push(MODULE_TO_MENU['ceo_audit']);
          }
        });
        setMenuItems(adminMenu);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        // Use /me endpoint which is accessible by any user
        const response = await axios.get(`${API}/api/user-access/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const permissions = response.data.effective_permissions || {};
        setUserPermissions(permissions);
        
        // Build menu based on permissions
        // Cash Flow only shows if user has explicit cash_flow permission
        const allowedMenu = [];
        MODULE_ORDER.forEach(key => {
          if (permissions[key] && MODULE_TO_MENU[key]) {
            allowedMenu.push(MODULE_TO_MENU[key]);
          }
          // Insert Reports after view_closings if user has any report permission
          if (key === 'view_closings' && hasAnyReportPermission(permissions)) {
            allowedMenu.push({ name: 'Reports', path: '/reports', icon: BarChart3 });
          }
        });
        
        setMenuItems(allowedMenu);
      } catch (error) {
        console.error('Failed to fetch permissions, using role defaults:', error);
        // Fall back to role-based menu
        setMenuItems(getRoleBasedMenu(user?.role));
      }
    };

    if (user?.id) {
      fetchPermissions();
    }
  }, [user?.id, user?.role]);

  // Fetch pending stock count for bar/kitchen/cafe users
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!['bartender', 'kitchen', 'cafe'].includes(user?.role)) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/store-ledger/pending-issues/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        if (user.role === 'bartender') setPendingCount(data.bar || 0);
        else if (user.role === 'kitchen') setPendingCount(data.kitchen || 0);
        else if (user.role === 'cafe') setPendingCount(data.cafe || 0);
      } catch (error) {
        console.error('Failed to fetch pending count:', error);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleNotificationClick = () => {
    if (user?.role === 'bartender') navigate('/bar-master');
    else if (user?.role === 'kitchen') navigate('/kitchen-master');
    else if (user?.role === 'cafe') navigate('/cafe-master');
  };

  const getRoleLabel = () => {
    const labels = {
      admin: 'Admin/Owner',
      partner: 'Partner',
      general_manager: 'General Manager',
      accountant: 'Accountant',
      team_member: 'Team Member',
      storekeeper: 'Storekeeper',
      bartender: 'Bartender',
      kitchen: 'Kitchen',
      cafe: 'Cafe',
      cashier: 'Cashier',
    };
    return labels[user?.role] || 'User';
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    onLogout();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Hoppers DSR</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`menu-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500">{getRoleLabel()}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-bold" style={{ fontFamily: 'Space Grotesk' }}>Hoppers DSR</span>
          <div className="flex items-center gap-2">
            {/* Notification Bell for Bar/Kitchen/Cafe */}
            {['bartender', 'kitchen', 'cafe'].includes(user?.role) && (
              <button
                onClick={handleNotificationClick}
                className="relative p-2 hover:bg-gray-100 rounded-full"
                title="Pending Stock from Store"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {pendingCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                    {pendingCount}
                  </Badge>
                )}
              </button>
            )}
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          </div>
        </header>

        {/* Desktop notification bell */}
        {['bartender', 'kitchen', 'cafe'].includes(user?.role) && (
          <div className="hidden lg:flex justify-end p-4 pb-0">
            <button
              onClick={handleNotificationClick}
              className="relative p-2 hover:bg-gray-100 rounded-full border border-gray-200"
              title="Pending Stock from Store"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {pendingCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                  {pendingCount}
                </Badge>
              )}
            </button>
          </div>
        )}

        {children}
      </main>
    </div>
  );
};

export default Layout;
